import { supabase } from '@/lib/supabase'

const runtimeEnv = typeof window !== 'undefined' ? window.__DRVN_ENV__ || {} : {}
const vapidPublicKey = process.env.VUE_APP_VAPID_PUBLIC_KEY || runtimeEnv.VUE_APP_VAPID_PUBLIC_KEY || ''
const baseServiceWorkerUrl = `${process.env.BASE_URL || '/'}service-worker.js`
const serviceWorkerUrl = baseServiceWorkerUrl.startsWith('//')
  ? baseServiceWorkerUrl
  : baseServiceWorkerUrl.replace(/\/{2,}/g, '/')

const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms))

const withTimeout = async (promise, timeoutMs, timeoutMessage) => {
  let timerId = null

  return Promise.race([
    promise.finally(() => {
      if (timerId) {
        window.clearTimeout(timerId)
      }
    }),
    new Promise((_, reject) => {
      timerId = window.setTimeout(() => {
        reject(new Error(timeoutMessage))
      }, timeoutMs)
    }),
  ])
}

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)))
}

const getValidatedVapidKey = () => {
  if (!vapidPublicKey) {
    throw new Error('Missing VAPID public key configuration.')
  }

  const keyBytes = urlBase64ToUint8Array(vapidPublicKey)
  if (keyBytes.length !== 65) {
    throw new Error(`Invalid VAPID public key in frontend config. Expected 65 bytes after decoding, got ${keyBytes.length}.`)
  }

  return keyBytes
}

export const isPushSupported = () => {
  if (typeof window === 'undefined') return false
  return Boolean(window.isSecureContext && 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window)
}

export const getPushPermissionState = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported'
  return Notification.permission || 'default'
}

const getServiceWorkerRegistrations = async () => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return []

  try {
    const registrations = await navigator.serviceWorker.getRegistrations()
    const readyRegistration = await withTimeout(
      navigator.serviceWorker.ready,
      2500,
      'Timed out waiting for the service worker to become active.'
    ).catch(() => null)
    const merged = [...registrations, readyRegistration].filter(Boolean)
    return merged.filter((registration, index) => merged.indexOf(registration) === index)
  } catch (_error) {
    return []
  }
}

const getPushRegistration = async () => {
  const registrations = await getServiceWorkerRegistrations()
  const activeRegistration = registrations.find((registration) => registration?.active || registration?.waiting || registration?.installing)
  if (activeRegistration) {
    return activeRegistration
  }

  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return null
  }

  try {
    const registration = await navigator.serviceWorker.register(serviceWorkerUrl)
    if (registration) {
      for (let attempt = 0; attempt < 20; attempt += 1) {
        if (registration.active || registration.waiting) {
          return registration
        }
        await wait(150)
      }
      return registration
    }
  } catch (_error) {
    return null
  }

  return null
}

const getExistingPushSubscription = async () => {
  const registrations = await getServiceWorkerRegistrations()

  for (const registration of registrations) {
    try {
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        return { registration, subscription }
      }
    } catch (_error) {
      // Ignore bad registrations and keep looking.
    }
  }

  return { registration: null, subscription: null }
}

export const hasPushSubscription = async () => {
  if (typeof window === 'undefined') return false
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false

  try {
    const { subscription } = await getExistingPushSubscription()
    return Boolean(subscription)
  } catch (_error) {
    return false
  }
}

export const getPushUnsupportedReason = () => {
  if (typeof window === 'undefined') return 'Push notifications are only available in the browser.'
  if (!window.isSecureContext) return 'Push notifications require HTTPS.'
  if (!('Notification' in window)) return 'This browser does not support system notifications.'
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return 'This browser does not support push notifications yet.'
  }
  return ''
}

const getDeviceLabel = () => {
  if (typeof navigator === 'undefined') return 'browser'
  const ua = navigator.userAgent || ''
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches || window.navigator?.standalone === true
  if (/iPhone|iPad|iPod/i.test(ua)) return standalone ? 'ios-pwa' : 'ios-browser'
  if (/Android/i.test(ua)) return standalone ? 'android-pwa' : 'android-browser'
  return standalone ? 'desktop-pwa' : 'desktop-browser'
}

export const syncPushSubscription = async (session) => {
  if (!session?.user?.id || !isPushSupported() || !vapidPublicKey) return null
  if (Notification.permission !== 'granted') return null

  const { subscription: existingSubscription } = await getExistingPushSubscription()
  const registration = await getPushRegistration()
  let subscription = existingSubscription

  if (!registration) {
    throw new Error('No active service worker was found for push notifications on this device.')
  }

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: getValidatedVapidKey(),
    })
  }

  const subscriptionJson = subscription.toJSON()
  const keys = subscriptionJson.keys || {}

  const { error } = await supabase
    .from('push_subscriptions')
    .upsert({
      user_id: session.user.id,
      endpoint: subscription.endpoint,
      p256dh: keys.p256dh || '',
      auth: keys.auth || '',
      user_agent: navigator.userAgent || '',
      device_label: getDeviceLabel(),
      last_seen_at: new Date().toISOString(),
    }, {
      onConflict: 'endpoint',
    })

  if (error) {
    throw error
  }

  return subscription
}

export const requestPushPermissionAndSubscribe = async (session) => {
  if (!isPushSupported()) {
    throw new Error(getPushUnsupportedReason() || 'Push notifications are not supported on this device.')
  }
  if (!session?.user?.id) {
    throw new Error('Your session is not ready yet. Please wait a moment and try again.')
  }
  getValidatedVapidKey()

  let permission = Notification.permission
  if (permission === 'default') {
    permission = await Notification.requestPermission()
  }

  if (permission !== 'granted') {
    throw new Error(permission === 'denied'
      ? 'Notifications are blocked in this browser. Enable them from browser settings.'
      : 'Notification permission was not granted.')
  }

  const subscription = await syncPushSubscription(session)
  if (!subscription) {
    throw new Error('Notification permission was granted, but this device subscription could not be created.')
  }

  return subscription
}

export const unsubscribePushNotifications = async (session) => {
  if (!session?.user?.id || !isPushSupported()) return

  const registrations = await getServiceWorkerRegistrations()

  for (const registration of registrations) {
    const subscription = await registration.pushManager.getSubscription()

    if (subscription) {
      await supabase.from('push_subscriptions').delete().eq('endpoint', subscription.endpoint).eq('user_id', session.user.id)
      await subscription.unsubscribe()
    }
  }
}

export const triggerNotificationPush = async (notificationId) => {
  if (!notificationId) return
  try {
    await supabase.functions.invoke('send-notification-push', {
      body: { notificationId },
    })
  } catch (_error) {
    // Push delivery is best-effort and should not block engagement actions.
  }
}
